
import { useCallback, useState } from 'react';
import { cellKey, parseCellKey, type CellKey } from './cell-instants.ts';

export interface SlotPaintApi {
  beginStroke: (key: CellKey) => Set<CellKey>;
  extendStroke: (key: CellKey) => Set<CellKey> | null;
  endStroke: () => void;
  isPainting: boolean;

  armTap: (key: CellKey) => boolean;
  commitTap: (key: CellKey) => Set<CellKey> | null;
  cancelTap: () => void;
  armedAnchor: { day: number; slot: number } | null;
}

export interface UseSlotPaintArgs {
  painted: ReadonlySet<CellKey>;
  canPaint?: (key: CellKey) => boolean;
}

interface Stroke {
  readonly paint: boolean;
  readonly anchor: { day: number; slot: number };
  readonly base: ReadonlySet<CellKey>;
}

function fillColumn(
  stroke: Stroke,
  focus: { day: number; slot: number },
  allowed: (key: CellKey) => boolean,
): Set<CellKey> {
  const next = new Set(stroke.base);
  const day = stroke.anchor.day;
  const sLo = Math.min(stroke.anchor.slot, focus.slot);
  const sHi = Math.max(stroke.anchor.slot, focus.slot);
  for (let slot = sLo; slot <= sHi; slot++) {
    const key = cellKey(day, slot);
    if (stroke.paint) {
      if (allowed(key)) next.add(key);
    } else {
      next.delete(key);
    }
  }
  return next;
}

export function useSlotPaint({ painted, canPaint }: UseSlotPaintArgs): SlotPaintApi {
  const [stroke, setStroke] = useState<Stroke | null>(null);

  const allowed = useCallback((key: CellKey) => (canPaint ? canPaint(key) : true), [canPaint]);

  const beginStroke = useCallback(
    (key: CellKey): Set<CellKey> => {
      if (!allowed(key)) {
        return new Set(painted);
      }
      const paint = !painted.has(key);
      const anchor = parseCellKey(key);
      const armed: Stroke = { paint, anchor, base: new Set() };
      setStroke(armed);
      return fillColumn(armed, anchor, allowed);
    },
    [painted, allowed],
  );

  const extendStroke = useCallback(
    (key: CellKey): Set<CellKey> | null => {
      if (!stroke) return null;
      return fillColumn(stroke, parseCellKey(key), allowed);
    },
    [stroke, allowed],
  );

  const endStroke = useCallback(() => setStroke(null), []);

  const armTap = useCallback(
    (key: CellKey): boolean => {
      if (!allowed(key)) {
        setStroke(null);
        return false;
      }
      const paint = !painted.has(key);
      setStroke({ paint, anchor: parseCellKey(key), base: new Set() });
      return true;
    },
    [painted, allowed],
  );

  const commitTap = useCallback(
    (key: CellKey): Set<CellKey> | null => {
      if (!stroke) return null;
      setStroke(null);
      if (!allowed(key)) return null;
      return fillColumn(stroke, parseCellKey(key), allowed);
    },
    [stroke, allowed],
  );

  const cancelTap = useCallback(() => setStroke(null), []);

  return {
    beginStroke,
    extendStroke,
    endStroke,
    isPainting: stroke !== null,
    armTap,
    commitTap,
    cancelTap,
    armedAnchor: stroke?.anchor ?? null,
  };
}
